import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, RotateCcw, Clock, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { purchaseOrdersAPI } from '../../lib/api';
import { DOC_LABELS, apiErrorText } from './poDocumentRules';

const labelText = (v) => DOC_LABELS.find((l) => l.value === v)?.label || 'Other';

const StatusIcon = ({ status }) => {
  if (status === 'done') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
  if (status === 'uploading') return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  return <Clock className="h-4 w-4 text-muted-foreground" />;
};

// Shown in the New PO drawer after the PO exists: uploads each picked file, with retry for failures.
export const PostCreateUploads = ({ po, files, onStatusChange, onClose }) => {
  const [items, setItems] = useState(() => files.map((f) => ({ ...f, status: 'pending', pct: 0, error: null })));
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const patch = (i, p) => setItems((prev) => prev.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const run = useCallback(async (indexes) => {
    setBusy(true);
    for (const i of indexes) {
      patch(i, { status: 'uploading', pct: 0, error: null });
      try {
        await purchaseOrdersAPI.uploadDocument(po.id, files[i].file, files[i].label, 'create',
          (e) => e.total && patch(i, { pct: Math.round((e.loaded / e.total) * 100) }));
        patch(i, { status: 'done', pct: 100 });
      } catch (err) {
        patch(i, { status: 'failed', error: apiErrorText(err, 'Upload failed. Check your connection and retry.') });
      }
    }
    setBusy(false);
  }, [po.id, files]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run(files.map((_, i) => i));
  }, [files, run]);

  const done = items.filter((x) => x.status === 'done').length;
  const failed = items.map((x, i) => (x.status === 'failed' ? i : -1)).filter((i) => i >= 0);
  const finished = !busy && items.every((x) => x.status === 'done' || x.status === 'failed');
  const needsAttention = busy || failed.length > 0;

  useEffect(() => { onStatusChange?.({ busy, failedCount: failed.length }); }, [busy, failed.length, onStatusChange]);

  useEffect(() => {
    if (!needsAttention) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [needsAttention]);

  return (
    <div className="space-y-5" data-testid="new-po-upload-status">
      <div className="rounded-md border bg-muted/40 p-4">
        <p className="text-sm font-medium" data-testid="new-po-upload-summary">
          PO {po.po_number} was created.{' '}
          {busy ? `Attaching documents… ${done} of ${items.length} done.`
            : failed.length ? `${done} of ${items.length} documents attached, ${failed.length} failed.`
              : `All ${items.length} document${items.length === 1 ? '' : 's'} attached.`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {busy ? 'Please keep this window open until uploads finish.'
            : failed.length ? 'Retry adds the files to this same PO; no new PO is created.' : 'You can close this window.'}
        </p>
      </div>
      <div className="space-y-2">
        {items.map((x, i) => (
          <div key={`${x.name}-${i}`} className="rounded-md border px-3 py-2" data-testid={`new-po-upload-${i}`}>
            <div className="flex items-center gap-3">
              <StatusIcon status={x.status} />
              <span className="min-w-0 flex-1 truncate text-sm">{x.name}</span>
              <span className="text-xs text-muted-foreground">{labelText(x.label)}</span>
              {x.status === 'failed' && !busy && (
                <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => run([i])} data-testid={`new-po-upload-retry-${i}`}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry
                </Button>
              )}
            </div>
            {x.status === 'uploading' && <Progress value={x.pct} className="mt-2 h-1.5" />}
            {x.error && <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid={`new-po-upload-error-${i}`}>{x.error}</p>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
        {failed.length > 1 && !busy && (
          <Button type="button" variant="outline" onClick={() => run(failed)} data-testid="new-po-upload-retry-all">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Retry all failed
          </Button>
        )}
        <Button type="button" disabled={!finished} onClick={onClose} variant={failed.length ? 'secondary' : 'default'} data-testid="new-po-upload-close">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Paperclip className="mr-1.5 h-4 w-4" />}
          {failed.length ? 'Close without failed files' : 'Done'}
        </Button>
      </div>
    </div>
  );
};
